// services/token.service.js

class TokenService {
  constructor() {
    this.TOKEN_KEY = 'access_token'
    this.REFRESH_TOKEN_KEY = 'refresh_token'
    this.ID_TOKEN_KEY = 'id_token'
    this.EXPIRES_AT_KEY = 'token_expires_at'
    this.USER_DATA_KEY = 'user_data'
  }

  // =============================================================================
  // GESTIÓN DE TOKENS
  // =============================================================================

  /**
   * Guardar tokens en localStorage
   * @param {Object} tokens - Objeto con access_token, refresh_token, id_token, expires_in
   */
  saveTokens(tokens) {
    try {
      if (tokens.access_token) {
        localStorage.setItem(this.TOKEN_KEY, tokens.access_token)
      }
      
      if (tokens.refresh_token) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token)
      }
      
      if (tokens.id_token) {
        localStorage.setItem(this.ID_TOKEN_KEY, tokens.id_token)
      }
      
      // Calcular tiempo de expiración
      if (tokens.expires_in) {
        const expiresAt = new Date(Date.now() + (tokens.expires_in * 1000))
        localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt.toISOString())
      }
      
    } catch (error) {
      throw new Error('Failed to save tokens')
    }
  }

  /**
   * Obtener access token
   * @returns {string|null} Access token o null si no existe
   */
  getAccessToken() {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY)
      return token
    } catch (error) {
      return null
    }
  }

  /**
   * Obtener refresh token
   * @returns {string|null} Refresh token o null si no existe
   */
  getRefreshToken() {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY)
    } catch (error) {
      return null
    }
  }

  /**
   * Obtener ID token
   * @returns {string|null} ID token o null si no existe
   */
  getIdToken() {
    try {
      return localStorage.getItem(this.ID_TOKEN_KEY)
    } catch (error) {
      return null
    }
  }

  /**
   * Obtener fecha de expiración del token
   * @returns {Date|null} Fecha de expiración o null si no existe
   */
  getTokenExpirationDate() {
    try {
      const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY)
      return expiresAt ? new Date(expiresAt) : null
    } catch (error) {
      return null
    }
  }

  /**
   * Verificar si el token ha expirado
   * @param {number} bufferMinutes - Minutos de buffer antes de considerar expirado (default: 5)
   * @returns {boolean} true si el token expiró
   */
  isTokenExpired(bufferMinutes = 5) {
    try {
      const expirationDate = this.getTokenExpirationDate()
      if (!expirationDate) return true
      
      const now = new Date()
      const bufferTime = bufferMinutes * 60 * 1000 // convertir a milliseconds
      
      return now >= (expirationDate.getTime() - bufferTime)
    } catch (error) {
      return true // Si hay error, asumir que expiró
    }
  }

  /**
   * Obtener tiempo restante hasta expiración
   * @returns {number} Minutos restantes hasta expiración (puede ser negativo si ya expiró)
   */
  getTimeUntilExpiration() {
    try {
      const expirationDate = this.getTokenExpirationDate()
      if (!expirationDate) return -1
      
      const now = new Date()
      const diffMs = expirationDate.getTime() - now.getTime()
      return Math.floor(diffMs / (1000 * 60)) // convertir a minutos
    } catch (error) {
      return -1
    }
  }

  /**
   * Verificar si existe un token válido
   * @returns {boolean} true si existe un token válido
   */
  hasValidToken() {
    const token = this.getAccessToken()
    const isExpired = this.isTokenExpired()
    
    return token && !isExpired
  }

  // =============================================================================
  // GESTIÓN DE DATOS DE USUARIO
  // =============================================================================

  /**
   * Guardar datos del usuario
   * @param {Object} userData - Datos del usuario
   */
  saveUserData(userData) {
    try {
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData))
    } catch (error) {
      throw new Error('Failed to save user data')
    }
  }

  /**
   * Obtener datos del usuario actual
   * @returns {Object|null} Datos del usuario o null si no existen
   */
  getUserData() {
    try {
      const userData = localStorage.getItem(this.USER_DATA_KEY)
      return userData ? JSON.parse(userData) : null
    } catch (error) {
      return null
    }
  }

  /**
   * Obtener ID del usuario actual
   * @returns {string|null} ID del usuario o null si no existe
   */
  getUserId() {
    const userData = this.getUserData()
    return userData?.user_id || userData?.id || null
  }

  /**
   * Obtener email del usuario actual
   * @returns {string|null} Email del usuario o null si no existe
   */
  getUserEmail() {
    const userData = this.getUserData()
    return userData?.email || null
  }

  /**
   * Verificar si el email del usuario está verificado
   * @returns {boolean} true si el email está verificado
   */
  isEmailVerified() {
    const userData = this.getUserData()
    return userData?.email_verified || false
  }

  // =============================================================================
  // LIMPIEZA DE DATOS
  // =============================================================================

  /**
   * Limpiar solo los tokens (mantener datos de usuario)
   */
  clearTokens() {
    try {
      localStorage.removeItem(this.TOKEN_KEY)
      localStorage.removeItem(this.REFRESH_TOKEN_KEY)
      localStorage.removeItem(this.ID_TOKEN_KEY)
      localStorage.removeItem(this.EXPIRES_AT_KEY)
    } catch (error) {
      // Error silencioso
    }
  }

  /**
   * Limpiar solo los datos de usuario (mantener tokens)
   */
  clearUserData() {
    try {
      localStorage.removeItem(this.USER_DATA_KEY)
    } catch (error) {
      // Error silencioso
    }
  }

  /**
   * Limpiar todos los datos de autenticación
   */
  clearAll() {
    try {
      this.clearTokens()
      this.clearUserData()
    } catch (error) {
      // Error silencioso
    }
  }

  // =============================================================================
  // DECODIFICACIÓN DE TOKENS JWT
  // =============================================================================

  /**
   * Decodificar payload de un JWT (sin verificar firma)
   * @param {string} token - Token JWT
   * @returns {Object|null} Payload decodificado o null si es inválido
   */
  decodeJWT(token) {
    try {
      if (!token || typeof token !== 'string') {
        return null
      }

      const parts = token.split('.')
      if (parts.length !== 3) {
        return null
      }

      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decoded)
    } catch (error) {
      return null
    }
  }

  /**
   * Obtener información del access token actual
   * @returns {Object|null} Información del token o null si es inválido
   */
  getTokenInfo() {
    const token = this.getAccessToken()
    if (!token) return null

    const decoded = this.decodeJWT(token)
    if (!decoded) return null

    return {
      userId: decoded.sub || decoded.user_id,
      email: decoded.email,
      exp: decoded.exp,
      iat: decoded.iat,
      iss: decoded.iss,
      aud: decoded.aud,
      ...decoded
    }
  }

  /**
   * Verificar si el token JWT ha expirado según su campo 'exp'
   * @returns {boolean} true si el token ha expirado según JWT
   */
  isJWTExpired() {
    const tokenInfo = this.getTokenInfo()
    if (!tokenInfo || !tokenInfo.exp) return true

    const now = Math.floor(Date.now() / 1000)
    return now >= tokenInfo.exp
  }

  // =============================================================================
  // UTILIDADES
  // =============================================================================

  /**
   * Obtener resumen del estado de autenticación
   * @returns {Object} Estado completo de autenticación
   */
  getAuthState() {
    const hasToken = !!this.getAccessToken()
    const isExpired = this.isTokenExpired()
    const userData = this.getUserData()
    const tokenInfo = this.getTokenInfo()

    return {
      isAuthenticated: hasToken && !isExpired,
      hasToken,
      isExpired,
      timeUntilExpiration: this.getTimeUntilExpiration(),
      user: userData,
      tokenInfo,
      isEmailVerified: this.isEmailVerified()
    }
  }

  /**
   * Validar consistencia entre localStorage y JWT
   * @returns {boolean} true si los datos son consistentes
   */
  validateTokenConsistency() {
    try {
      const localExpiry = this.getTokenExpirationDate()
      const jwtInfo = this.getTokenInfo()
      
      if (!localExpiry || !jwtInfo || !jwtInfo.exp) {
        return false
      }

      const jwtExpiry = new Date(jwtInfo.exp * 1000)
      const timeDiff = Math.abs(localExpiry.getTime() - jwtExpiry.getTime())
      
      // Permitir diferencia de hasta 60 segundos
      return timeDiff <= 60000
    } catch (error) {
      return false
    }
  }

  /**
   * Generar headers de autorización
   * @returns {Object} Headers con Authorization o vacío si no hay token válido
   */
  getAuthHeaders() {
    if (!this.hasValidToken()) {
      return {}
    }

    return {
      'Authorization': `Bearer ${this.getAccessToken()}`
    }
  }

  // =============================================================================
  // EVENTOS Y MONITOREO
  // =============================================================================

  /**
   * Configurar monitoreo de expiración de token
   * @param {Function} callback - Función a ejecutar cuando el token esté por expirar
   * @param {number} warningMinutes - Minutos antes de expiración para ejecutar callback
   * @returns {number} ID del interval (para clearInterval)
   */
  setupExpirationMonitoring(callback, warningMinutes = 5) {
    return setInterval(() => {
      const timeLeft = this.getTimeUntilExpiration()
      
      if (timeLeft <= warningMinutes && timeLeft > 0) {
        if (typeof callback === 'function') {
          callback(timeLeft)
        }
      } else if (timeLeft <= 0) {
        if (typeof callback === 'function') {
          callback(0)
        }
      }
    }, 60000) // Verificar cada minuto
  }
}

// Exportar instancia singleton
export default new TokenService()
